/**
 * Which tenant Settings tab needs attention, for surfaces outside Dashboard.
 *
 * Settings moved out of the tenant tab strip into the top navbar
 * (Navigation.tsx), but the "action needed" pip has to travel with it —
 * otherwise a tenant with no date of birth never learns they have to add
 * one. Dashboard computes the same signal from the queries it already
 * runs; the navbar has none of them, so this hook re-derives it from two
 * cheap reads.
 *
 * The predicates deliberately mirror Dashboard's `needsProfileCompletion`
 * / `hasDocumentNotifications` / `settingsAlertTab`: profile completion
 * wins when both fire (COL-260), and a document only counts when it was
 * rejected *with* an admin note — a doc pending review is not something
 * the tenant can act on.
 */
import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";

export type TenantSettingsAlert = "profile" | "documents" | null;

export function useTenantSettingsAlert(enabled = true): TenantSettingsAlert {
  const { user } = useAuth();

  const { data } = useQuery<TenantSettingsAlert>({
    queryKey: ["tenant-settings-alert", user?.id],
    enabled: enabled && !!user?.id,
    // The navbar renders on every public page; this is a hint, not live
    // data, so keep it cheap and let Dashboard own the fresh copy.
    staleTime: 60_000,
    queryFn: async () => {
      const userId = user!.id;
      const [profileRes, docsRes] = await Promise.all([
        supabase
          .from("profiles")
          .select("date_of_birth")
          .eq("id", userId)
          .maybeSingle(),
        supabase
          .from("tenant_documents")
          .select("notes")
          .eq("user_id", userId)
          .eq("status", "rejected"),
      ]);
      if (profileRes.error) throw profileRes.error;
      if (docsRes.error) throw docsRes.error;

      const profile = profileRes.data as {
        date_of_birth?: string | null;
      } | null;
      // No profile row yet ⇒ nothing to nag about (matches Dashboard,
      // which returns false when `profile` is null).
      if (profile) {
        const dob = profile.date_of_birth;
        if (!dob || !String(dob).trim()) return "profile";
      }
      const rejectedWithNote = (
        (docsRes.data as { notes?: string | null }[]) ?? []
      ).some((doc) => Boolean(doc?.notes));
      return rejectedWithNote ? "documents" : null;
    },
  });

  return data ?? null;
}
